import { HomeArticlesQueryResult, useHomeArticlesLazyQuery } from '@graphql/queries/__generated__/home.generated'
import { Theme } from '@mui/material'
import Skeleton from '@mui/material/Skeleton'
import makeStyles from '@mui/styles/makeStyles'
import PlaceHolder from 'images/placeholder-img.svg'
import _ from 'lodash'
import React, { useEffect, useMemo, useRef } from 'react'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import { Link } from 'react-router-dom'
import { SwiperOptions } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { getBlogArticleUrl } from '../../../urls'
import { maybe } from '../../../utils/misc'
import { useTranslation } from 'react-i18next'
import { useIsVisible } from '../../../hooks/useIsVisible'

const MAX_SHOW_ARTICLES = 8
const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    // padding: '2% 4%'
  },
  imageWrapper: {
    paddingBottom: '60%',
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      paddingBottom: '25%',
    },
  },
}))

const ArticlesSkeleton: React.FC<{ swiperOptions: SwiperOptions }> = ({ swiperOptions }) => {
  const classes = useStyles()
  return (
    <Swiper {...swiperOptions}>
      {_.range(MAX_SHOW_ARTICLES).map((id, idx) => {
        return (
          <SwiperSlide key={id}>
            <div key={id} className={`relative flex md:flex-col`}>
              <div className={`${classes.imageWrapper} w-2/5 md:w-full`}>
                <LazyLoadImage
                  alt='Skeleton Article'
                  effect='opacity'
                  src={PlaceHolder}
                  className='w-full h-full object-cover'
                  wrapperClassName='absolute inset-0'
                />
              </div>
              <div className='flex-1 md:w-full pl-10 sm:pl-20 md:pl-0'>
                <h4 className='mt-0 md:mt-20 mb-10'>
                  <Skeleton width={200} />
                </h4>
                <div>
                  <Skeleton width={150} />
                  <Skeleton width={150} />
                  <Skeleton width={100} />
                </div>
              </div>
            </div>
          </SwiperSlide>
        )
      })}
    </Swiper>
  )
}

interface ArticlesProps {}

const Articles: React.FC<ArticlesProps> = ({}) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const ref = useRef()
  const isVisible = useIsVisible(ref, { threshold: 0.25 })

  const [loadArticles, articlesQueryResult] = useHomeArticlesLazyQuery({
    variables: {
      first: 20,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })

  useEffect(() => {
    if (isVisible && !articlesQueryResult.data) {
      loadArticles()
    }
  }, [isVisible, articlesQueryResult.data])

  const articles: HomeArticlesQueryResult['data']['blogArticleList']['edges'][0]['node'][] = useMemo(
    () =>
      maybe(
        () =>
          articlesQueryResult.data.blogArticleList.edges
            .map(edge => edge.node)
            .filter(node => node.categories.length > 0 && node.categories[0].blog)
            .slice(0, MAX_SHOW_ARTICLES),
        []
      ),
    [articlesQueryResult.data]
  )
  const swiperOptions: SwiperOptions = useMemo(
    () => ({
      spaceBetween: 20,
      slidesPerView: 4,
      lazy: true,
      preloadImages: false,
      pagination: {
        clickable: true,
        el: `#swiper-pagination-articles`,
      },
      watchSlidesProgress: true,
      loop: false,
      allowSlideNext: articles.length > 2,
      allowSlidePrev: articles.length > 2,
      breakpoints: {
        0: {
          spaceBetween: 0,
          slidesPerView: 1,
          speed: 300,
          grid: {
            fill: 'row',
            rows: 4,
          },
        },
        1000: {
          spaceBetween: 20,
          slidesPerView: 4,
          slidesPerGroup: 4,
          speed: 500,
        },
      },
    }),
    [articles.length]
  )

  return (
    <section className='px-10 md:px-40 lg:px-60 pb-50' ref={ref}>
      <h2
        className='text-center pt-20 md:pt-50 pb-40 font-freight'
        style={{
          fontSize: 'calc(50px + (70 - 50) * ((100vw - 320px) / (1440 - 320)))',
        }}
      >
        {t('title.newStories', 'New Stories')}
      </h2>
      {!articles.length && articlesQueryResult.loading && <ArticlesSkeleton swiperOptions={swiperOptions} />}
      {articles.length > 0 && (
        <>
          <Swiper {...swiperOptions}>
            {articles.map((node, idx) => {
              const blog = node.categories[0].blog
              return (
                <SwiperSlide key={node.id}>
                  <div className={`relative pt-20`}>
                    <Link
                      to={getBlogArticleUrl(blog.slug, node.slug)}
                      className='flex md:flex-col no-underline items-start'
                    >
                      <div className={`${classes.imageWrapper} w-2/5 md:w-full`}>
                        <LazyLoadImage
                          alt={node.title}
                          effect='opacity'
                          placeholderSrc={PlaceHolder}
                          src={node.mediumThumbnailWebp}
                          className='w-full h-full object-cover'
                          wrapperClassName='absolute inset-0'
                        />
                      </div>
                      <div className='flex-1 md:w-full pl-10 sm:pl-20 md:pl-0'>
                        <h4 className='text-base mt-0 md:mt-20 mb-10 font-bold tracking-2 leading-snug'>
                          {node.title}
                        </h4>
                        <div className='text-lg sm:text-2xl leading-tight tracking-wide line-clamp-4 md:line-clamp-6 font-freight'>
                          {node.annotation}
                        </div>
                      </div>
                    </Link>
                  </div>
                </SwiperSlide>
              )
            })}
          </Swiper>
          <div
            id={`swiper-pagination-articles`}
            className={`swiper-pagination swiper-pagination-clickable swiper-pagination-bullets w-full ${
              articles.length > 1 ? 'block' : 'hidden'
            }`}
          />
        </>
      )}
    </section>
  )
}

export default Articles
